/**
 * NetworkingAppointments - 1:1 meeting scheduler for delegates
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Plus, Calendar, Clock, MapPin, Check, X, Loader2, Users } from 'lucide-react';
import { appointmentAPI, getErrorMessage } from '@/lib/api';
import api from '@/lib/api';

const STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', completed: 'bg-blue-100 text-blue-800' };
const DURATIONS = [15, 30, 45, 60];

// Form component extracted outside
const AppointmentForm = ({ delegates, onSubmit, onCancel }) => {
  const [form, setForm] = useState({ target_id: '', scheduled_time: '', duration_minutes: 15, location: '', topic: '', message: '' });
  return (
    <div className="space-y-4">
      <div><Label>Meet With *</Label>
        <Select value={form.target_id} onValueChange={v => setForm({...form, target_id: v})}>
          <SelectTrigger><SelectValue placeholder="Select attendee..." /></SelectTrigger>
          <SelectContent>{delegates.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name} - {d.profile?.company || 'N/A'}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Date & Time *</Label><Input type="datetime-local" value={form.scheduled_time} onChange={e => setForm({...form, scheduled_time: e.target.value})} /></div>
        <div><Label>Duration</Label>
          <Select value={form.duration_minutes.toString()} onValueChange={v => setForm({...form, duration_minutes: parseInt(v)})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{DURATIONS.map(d => <SelectItem key={d} value={d.toString()}>{d} mins</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Networking Lounge" /></div>
        <div><Label>Topic</Label><Input value={form.topic} onChange={e => setForm({...form, topic: e.target.value})} placeholder="Partnership Discussion" /></div>
      </div>
      <div><Label>Message</Label><Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={2} placeholder="I'd love to discuss..." /></div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.target_id || !form.scheduled_time}>Send Request</Button>
      </div>
    </div>
  );
};

const NetworkingAppointments = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const { data: appointments = [], isLoading } = useQuery({
    queryKey: ['appointments', eventId],
    queryFn: () => appointmentAPI.list(eventId).then(r => r.data),
  });

  const { data: delegates = [] } = useQuery({
    queryKey: ['networking-delegates', eventId],
    queryFn: () => api.get(`/events/${eventId}/networking/roster`).then(r => r.data).catch(() => []),
  });

  const createMutation = useMutation({
    mutationFn: (data) => appointmentAPI.create(eventId, data),
    onSuccess: () => { toast.success('Meeting request sent'); queryClient.invalidateQueries(['appointments', eventId]); setIsOpen(false); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentAPI.update(eventId, id, data),
    onSuccess: () => { toast.success('Appointment updated'); queryClient.invalidateQueries(['appointments', eventId]); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const cancelMutation = useMutation({
    mutationFn: (id) => appointmentAPI.cancel(eventId, id),
    onSuccess: () => { toast.success('Appointment cancelled'); queryClient.invalidateQueries(['appointments', eventId]); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const formatDateTime = (dt) => dt ? new Date(dt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';
  const upcoming = appointments.filter(a => a.status === 'pending' || a.status === 'confirmed').sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
  const past = appointments.filter(a => a.status === 'completed' || a.status === 'cancelled');

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="networking-appointments">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6" />Networking Appointments</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Request Meeting</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Request a Meeting</DialogTitle></DialogHeader>
            <AppointmentForm delegates={delegates} onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {upcoming.length > 0 && (
        <div>
          <h3 className="font-semibold text-lg mb-3">Upcoming</h3>
          <div className="space-y-3">
            {upcoming.map(apt => (
              <Card key={apt.id} data-testid={`appointment-${apt.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar><AvatarFallback>{apt.target_id?.charAt(0) || '?'}</AvatarFallback></Avatar>
                      <div>
                        <p className="font-medium">{apt.topic || 'Meeting Request'}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{formatDateTime(apt.scheduled_time)}</span>
                          <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{apt.duration_minutes} mins</span>
                          {apt.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{apt.location}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={STATUS_COLORS[apt.status]}>{apt.status}</Badge>
                      {apt.status === 'pending' && (
                        <>
                          <Button size="sm" variant="outline" className="text-green-600" onClick={() => updateMutation.mutate({ id: apt.id, data: { status: 'confirmed' }})}><Check className="h-4 w-4" /></Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => updateMutation.mutate({ id: apt.id, data: { status: 'cancelled' }})}><X className="h-4 w-4" /></Button>
                        </>
                      )}
                      {apt.status === 'confirmed' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => cancelMutation.mutate(apt.id)}>Cancel</Button>}
                    </div>
                  </div>
                  {apt.message && <p className="text-sm text-slate-600 mt-2 pl-14">{apt.message}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <h3 className="font-semibold text-lg mb-3 text-slate-500">Past</h3>
          <div className="space-y-2 opacity-60">
            {past.slice(0, 5).map(apt => (
              <Card key={apt.id}>
                <CardContent className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm">{apt.topic || 'Meeting'}</span>
                    <span className="text-xs text-slate-500">{formatDateTime(apt.scheduled_time)}</span>
                  </div>
                  <Badge className={STATUS_COLORS[apt.status]}>{apt.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {appointments.length === 0 && <p className="text-center text-slate-500 py-8">No appointments scheduled. Request a meeting to start networking!</p>}
    </div>
  );
};

export default NetworkingAppointments;
